import { api } from "./api";
import type {
  Category,
  Product
} from "../types/catalog";

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

interface ProductListResponse extends ApiResponse<Product[]> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export async function getCategories(): Promise<Category[]> {
  const response = await api.get<ApiResponse<Category[]>>(
    "/categories"
  );

  return response.data.data;
}

export async function getProducts(
  category?: string
): Promise<Product[]> {
  const response = await api.get<ProductListResponse>(
    "/products",
    {
      params: category
        ? {
            category
          }
        : undefined
    }
  );

  return response.data.data;
}

export async function getProduct(
  slug: string
): Promise<Product> {
  const response = await api.get<ApiResponse<Product>>(
    `/products/${slug}`
  );

  return response.data.data;
}

export async function getBestSellers(
  limit = 8
): Promise<Product[]> {
  const response =
    await api.get<
      ApiResponse<Product[]>
    >(
      "/home/best-sellers",
      {
        params: {
          limit
        }
      }
    );

  return response.data.data;
}

export async function getFeaturedProducts(
  limit = 8
): Promise<Product[]> {
  const response =
    await api.get<
      ApiResponse<Product[]>
    >(
      "/home/featured-products",
      {
        params: {
          limit
        }
      }
    );

  return response.data.data;
}

export async function getNewArrivals(
  limit = 8
): Promise<Product[]> {
  const response =
    await api.get<
      ApiResponse<Product[]>
    >(
      "/home/new-arrivals",
      {
        params: {
          limit
        }
      }
    );

  return response.data.data;
}

export async function getHomeHero():
Promise<import("../types/admin").HomeHero | null> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").HomeHero | null
      >
    >(
      "/home/hero"
    );

  return response.data.data;
}

export function getPublicHomeHeroImageUrl(
  heroId: string,
  variant: "desktop" | "mobile",
  version?: string | null
): string {
  const baseUrl =
    String(api.defaults.baseURL ?? "")
      .replace(/\/$/, "");

  const url =
    `${baseUrl}/media/home-hero/${heroId}/${variant}`;

  if (!version) {
    return url;
  }

  return (
    `${url}?v=${encodeURIComponent(version)}`
  );
}

export async function getHomeSections():
Promise<import("../types/admin").HomeSection[]> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").HomeSection[]
      >
    >(
      "/home/sections"
    );

  return response.data.data;
}

export async function getMakingRini():
Promise<import("../types/admin").MakingRini | null> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").MakingRini | null
      >
    >(
      "/home/making-rini"
    );

  return response.data.data;
}

export function getMakingRiniMediaUrl(
  makingRiniId: string,
  mediaType: "poster" | "video",
  version?: string | null
): string {
  const baseUrl =
    String(api.defaults.baseURL ?? "")
      .replace(/\/$/, "");

  const url =
    `${baseUrl}/media/making-rini/${makingRiniId}/${mediaType}`;

  if (!version) {
    return url;
  }

  return (
    `${url}?v=${encodeURIComponent(version)}`
  );
}

export async function getFeaturedIngredients():
Promise<import("../types/admin").AdminIngredient[]> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").AdminIngredient[]
      >
    >(
      "/ingredients",
      {
        params: {
          featured: true
        }
      }
    );

  return response.data.data;
}

export function getPublicIngredientImageUrl(
  ingredientId: string,
  version?: string | null
): string {
  const baseUrl =
    String(
      api.defaults.baseURL ??
      ""
    ).replace(/\/$/, "");

  const url =
    `${baseUrl}/media/ingredient-images/${ingredientId}`;

  if (!version) {
    return url;
  }

  return (
    `${url}?v=${encodeURIComponent(version)}`
  );
}

export async function getFeaturedTestimonials():
Promise<import("../types/admin").AdminTestimonial[]> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").AdminTestimonial[]
      >
    >(
      "/testimonials",
      {
        params: {
          featured: true
        }
      }
    );

  return response.data.data;
}

export function getPublicTestimonialImageUrl(
  testimonialId: string,
  version?: string | null
): string {
  const baseUrl =
    String(
      api.defaults.baseURL ??
      ""
    ).replace(/\/$/, "");

  const url =
    `${baseUrl}/media/testimonial-images/${testimonialId}`;

  if (!version) {
    return url;
  }

  return (
    `${url}?v=${encodeURIComponent(version)}`
  );
}

export async function getHomepageInstagramPosts():
Promise<import("../types/admin").AdminInstagramPost[]> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/admin").AdminInstagramPost[]
      >
    >(
      "/instagram",
      {
        params: {
          homepage: true
        }
      }
    );

  return response.data.data;
}

export function getPublicInstagramImageUrl(
  postId: string,
  version?: string | null
): string {
  const baseUrl =
    String(
      api.defaults.baseURL ??
      ""
    ).replace(/\/$/, "");

  const url =
    `${baseUrl}/media/instagram-images/${postId}`;

  if (!version) {
    return url;
  }

  return (
    `${url}?v=${encodeURIComponent(version)}`
  );
}

export interface GiftHamperSection {
  id: string;
  eyebrow: string | null;
  title: string;
  description: string | null;
  primaryCtaText: string | null;
  primaryCtaUrl: string | null;
  secondaryCtaText: string | null;
  secondaryCtaUrl: string | null;
  desktopImageKey: string | null;
  mobileImageKey: string | null;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export async function getGiftHamperSection():
Promise<GiftHamperSection | null> {
  const response =
    await api.get<
      ApiResponse<
        GiftHamperSection | null
      >
    >(
      "/home/gift-hamper"
    );

  return response.data.data;
}

export function getPublicGiftHamperImageUrl(
  sectionId: string,
  variant: "desktop" | "mobile",
  version?: string | null
): string {
  const baseUrl =
    String(
      api.defaults.baseURL ??
      ""
    ).replace(/\/$/, "");

  const url =
    `${baseUrl}/media/gift-hampers/${sectionId}/${variant}`;

  if (!version) {
    return url;
  }

  return (
    `${url}?v=${encodeURIComponent(version)}`
  );
}

export function getPublicProductImageUrl(
  imageId: string
): string {
  const baseUrl =
    String(
      api.defaults.baseURL ??
      ""
    ).replace(/\/$/, "");

  return (
    `${baseUrl}/media/product-images/${imageId}`
  );
}

export async function getIngredients(
  featured = false
): Promise<
  import("../types/catalog").Ingredient[]
> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/catalog").Ingredient[]
      >
    >(
      "/ingredients",
      {
        params:
          featured
            ? {
                featured: true
              }
            : undefined
      }
    );

  return response.data.data;
}

export async function getIngredient(
  slug: string
): Promise<
  import("../types/catalog").Ingredient
> {
  const response =
    await api.get<
      ApiResponse<
        import("../types/catalog").Ingredient
      >
    >(
      `/ingredients/${slug}`
    );

  return response.data.data;
}


